import { DownloadOutlined, InfoCircleOutlined } from "@ant-design/icons";
import { useQuery } from "@tanstack/react-query";
import type { StockSummaryRow, StockSummaryScope } from "@wms/domain";
import { Alert, Button, Empty, Input, Popover, Select, Space, Table, Typography } from "antd";
import type { ColumnsType } from "antd/es/table";
import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { ApiError, api, errorMessage } from "../api/client";
import { BarcodeButton } from "../components/BarcodeButton";
import { HelpHint } from "../components/HelpHint";
import { LoadProblem } from "../components/LoadProblem";
import { OWNER_UNKNOWN, SUMMARY_COLUMNS, appliedFilters, positive, qtyWithUnit, summaryCsv, summaryParams } from "./stockSummary";

/** A label of a filter with its «(?)» (UXW-54): what the filter does, in one or two sentences. */
function FilterLabel({ text, help }: { text: string; help: string }) {
  return (
    <Typography.Text strong style={{ display: "inline-flex", alignItems: "center" }}>
      {text}
      <HelpHint subject={text} text={help} size={13} />
    </Typography.Text>
  );
}

/** UXW-38: the reasons of «Исключено» open by a click, a tap or the keyboard — not only by a colour or a hover. */
function ExcludedCell({ row }: { row: StockSummaryRow }) {
  const text = qtyWithUnit(row.excluded, row.unit);
  if (!positive(row.excluded)) return <>{text}</>;
  return (
    // the row opens the product card on a click: the button and its popover keep the click and the keys to themselves
    <span onClick={(e) => e.stopPropagation()} onKeyDown={(e) => e.stopPropagation()}>
      <Popover
        trigger="click"
        title="Почему не к отбору"
        content={
          <ul style={{ margin: 0, paddingLeft: 18, maxWidth: 360 }}>
            {row.excluded_reasons.map((reason) => (
              <li key={reason}>{reason}</li>
            ))}
          </ul>
        }
      >
        <Button type="link" size="small" style={{ padding: 0 }} data-testid="stock-excluded" aria-label={`Исключено ${text}: показать причины`}>
          <Typography.Text type="warning">
            {text} <InfoCircleOutlined />
          </Typography.Text>
        </Button>
      </Popover>
    </span>
  );
}

function downloadCsv(rows: StockSummaryRow[]) {
  const url = URL.createObjectURL(new Blob([summaryCsv(rows)], { type: "text/csv;charset=utf-8" }));
  const a = document.createElement("a");
  a.href = url;
  a.download = "ostatki-po-tovaram.csv";
  document.body.appendChild(a);
  a.click();
  a.remove();
  window.setTimeout(() => URL.revokeObjectURL(url), 1000);
}

/**
 * «Остатки по товарам». ТЗ «Рабочие столы» 1.1 (31.10.2026): the code of every product opens from its row (UXW-32,
 * the existing BarcodeButton — viewing never makes a new code), the server searches the name, article, code and
 * barcodes over the whole allowed selection (UXW-35/36), the warehouse and the owner of the stock fact are explicit
 * filters inside the user's warehouses (UXW-37), and every quantity has its unit (UXW-38). The meaning of the numbers
 * is the server's and is the same as before; only the column names are shorter, the former ones stay in the hints
 * and in the export.
 */
export function StockSummaryPage() {
  const navigate = useNavigate();
  const [typed, setTyped] = useState("");
  const [q, setQ] = useState("");
  const [warehouseId, setWarehouseId] = useState<string | null>(null);
  const [owner, setOwner] = useState<string | null>(null);
  const filters = { q, warehouseId, owner };
  const scope = useQuery({
    queryKey: ["stock", "summary-scope", warehouseId],
    queryFn: () => api.get<StockSummaryScope>("/stock/summary/scope", { warehouse_id: warehouseId }),
    staleTime: 60_000,
  });
  // UXW-36: every set of filters is its own query — a late answer of an older search never replaces the newer one
  const summary = useQuery({
    queryKey: ["stock", "summary", q, warehouseId, owner],
    queryFn: () => api.get<StockSummaryRow[]>("/stock/summary", summaryParams(filters)),
  });
  const reset = () => {
    setTyped("");
    setQ("");
    setWarehouseId(null);
    setOwner(null);
  };
  const warehouses = scope.data?.warehouses ?? [];
  const warehouseName = (id: string | null) => {
    const w = warehouses.find((x) => x.id === id);
    return w ? `${w.code ? `${w.code} — ` : ""}${w.name}` : undefined;
  };
  const ownerName = scope.data?.owners.find((o) => o.id === owner)?.name;
  const applied = appliedFilters(filters, { warehouse: warehouseName(warehouseId), owner: ownerName });
  const filtered = !!(q || warehouseId || owner);

  const numberColumns: ColumnsType<StockSummaryRow> = SUMMARY_COLUMNS.map((c) => ({
    key: c.field,
    title: (
      <span style={{ whiteSpace: "nowrap" }}>
        {c.title}
        <HelpHint subject={c.title} text={c.former !== c.title ? `${c.help} Прежнее название: «${c.former}».` : c.help} size={12} />
      </span>
    ),
    align: "right" as const,
    render: (_: unknown, row: StockSummaryRow) => {
      if (c.field === "locations") return row.locations;
      if (c.field === "excluded") return <ExcludedCell row={row} />;
      const text = qtyWithUnit(row[c.field], row.unit);
      return c.field === "available" ? <b>{text}</b> : text;
    },
    sorter:
      c.field === "quantity" ? (a: StockSummaryRow, b: StockSummaryRow) => Number(a.quantity) - Number(b.quantity) : undefined,
  }));

  return (
    <div>
      <Space style={{ width: "100%", justifyContent: "space-between", marginBottom: 12 }} wrap>
        <Typography.Title level={3} style={{ margin: 0 }}>
          Остатки по товарам
        </Typography.Title>
        <Button icon={<DownloadOutlined />} disabled={!summary.data?.length} onClick={() => summary.data && downloadCsv(summary.data)} data-testid="stock-summary-export">
          Выгрузить
        </Button>
      </Space>
      <Space wrap align="end" style={{ marginBottom: 12 }} data-testid="stock-summary-filters">
        <Space direction="vertical" size={4}>
          <FilterLabel text="Склад" help="Остатки только по выбранному складу. Без выбора — сумма по всем складам, доступным вашей роли." />
          <Select
            allowClear
            style={{ width: 240, maxWidth: "calc(100vw - 56px)" }}
            placeholder="Все доступные склады"
            loading={scope.isLoading}
            value={warehouseId ?? undefined}
            onChange={(v) => setWarehouseId(v ?? null)}
            options={warehouses.map((w) => ({ value: w.id, label: warehouseName(w.id) }))}
            data-testid="stock-summary-warehouse"
          />
        </Space>
        <Space direction="vertical" size={4}>
          <FilterLabel
            text="Клиент / владелец"
            help="Чей товар: владелец, записанный на самом остатке. Получатель заказа здесь не участвует. «Принадлежность требует сверки» — остатки, у которых владелец не записан."
          />
          <Select
            allowClear
            showSearch
            optionFilterProp="label"
            style={{ width: 260, maxWidth: "calc(100vw - 56px)" }}
            placeholder="Все владельцы"
            loading={scope.isLoading}
            value={owner ?? undefined}
            onChange={(v) => setOwner(v ?? null)}
            options={[
              ...(scope.data?.owners ?? []).map((o) => ({ value: o.id, label: o.name })),
              ...(scope.data?.unknown_owner ? [{ value: OWNER_UNKNOWN, label: "Принадлежность требует сверки" }] : []),
            ]}
            data-testid="stock-summary-owner"
          />
        </Space>
        <Space direction="vertical" size={4}>
          <FilterLabel
            text="Найти товар"
            help="Название, артикул, код, GTIN или штрихкод товара и его упаковок. Можно отсканировать сканером. Ищет сервер по всем остаткам, точное совпадение кода — первым."
          />
          <Input.Search
            allowClear
            placeholder="Название, артикул, код или ШК"
            style={{ width: 340, maxWidth: "calc(100vw - 56px)" }}
            value={typed}
            onChange={(e) => {
              setTyped(e.target.value);
              // the clear cross returns the whole allowed selection at once
              if (!e.target.value) setQ("");
            }}
            // UXW-36: the scanned or typed string goes as it is — leading zeros and GS1 separators kept
            onSearch={(value) => setQ(value.trim() ? value : "")}
            data-testid="stock-summary-search"
          />
        </Space>
      </Space>
      {scope.isError && (
        <Alert
          type="warning"
          showIcon
          style={{ marginBottom: 8 }}
          message="Не удалось загрузить список складов и владельцев для фильтров"
          description={errorMessage(scope.error)}
          action={
            <Button size="small" onClick={() => scope.refetch()}>
              Повторить
            </Button>
          }
        />
      )}
      {/* UXW-37: the scope of the numbers is said aloud — a sum over several warehouses never reads as one address */}
      <Typography.Paragraph type="secondary" style={{ marginBottom: 8 }} data-testid="stock-summary-scope">
        {warehouseId ? `Склад: ${warehouseName(warehouseId) ?? "выбранный"}.` : `Все доступные склады${warehouses.length ? ` (${warehouses.length})` : ""}: числа — сумма по ним.`}{" "}
        {owner ? (owner === OWNER_UNKNOWN ? "Только остатки без записанного владельца." : `Владелец: ${ownerName ?? "выбранный"}.`) : "Все владельцы."}{" "}
        «К отбору» считается по тем же правилам, что и планирование: годное состояние и не истёкший срок. Карантин, брак и
        просроченные партии — в «Исключено» с причиной.
      </Typography.Paragraph>
      {summary.isError && summary.error instanceof ApiError && summary.error.problem.status === 403 ? (
        // a refusal of the right is not «не удалось загрузить» and offers no retry
        <LoadProblem error={summary.error} />
      ) : summary.isError ? (
        <Alert
          type="error"
          showIcon
          data-testid="stock-summary-error"
          message="Не удалось загрузить остатки"
          description={errorMessage(summary.error)}
          action={
            <Button size="small" onClick={() => summary.refetch()}>
              Повторить
            </Button>
          }
        />
      ) : summary.isSuccess && !summary.data.length && filtered ? (
        <Empty
          data-testid="stock-summary-empty"
          description={
            <span>
              Товар не найден по выбранным условиям
              <br />
              <Typography.Text type="secondary">{applied.join(" · ")}</Typography.Text>
            </span>
          }
        >
          <Button onClick={reset} data-testid="stock-summary-reset">
            Сбросить поиск
          </Button>
        </Empty>
      ) : (
        <Table<StockSummaryRow>
          rowKey="storage_object_id"
          size="middle"
          loading={summary.isLoading || summary.isFetching}
          dataSource={summary.data ?? []}
          pagination={{ pageSize: 50, showTotal: (t) => `Товаров: ${t}` }}
          scroll={{ x: true }}
          locale={{ emptyText: summary.isSuccess ? "На складах нет остатков" : " " }}
          onRow={(row) => ({ onClick: () => navigate(`/storage_objects/${row.storage_object_id}`), style: { cursor: "pointer" } })}
          columns={[
            {
              key: "barcode",
              title: "ШК",
              width: 48,
              // UXW-32: the existing code window of the product; its click and Enter never open the row
              render: (_: unknown, row) => (
                <BarcodeButton entity="storage_objects" id={row.storage_object_id} unit={row.unit} label="Штрихкоды товара" tooltip="Штрихкоды товара и упаковок" />
              ),
            },
            {
              key: "code",
              title: "Код",
              dataIndex: "storage_object_code",
              sorter: (a, b) => a.storage_object_code.localeCompare(b.storage_object_code),
              render: (code: string, row) => (
                <span>
                  {code}
                  {row.storage_object_article && (
                    <Typography.Text type="secondary" style={{ display: "block", fontSize: 12 }}>
                      арт. {row.storage_object_article}
                    </Typography.Text>
                  )}
                </span>
              ),
            },
            { key: "name", title: "Товар", dataIndex: "storage_object_name" },
            ...numberColumns,
          ]}
        />
      )}
    </div>
  );
}
